import { Injectable } from '@angular/core';
import { Agreement } from '../models/Agreement';
import { PremiseInfo } from '../models/location';
import { NewRegistrationInfo } from '../models/NewRegistrationInfo';

export enum SessionManagerKey {
  AmerenNonServiceAccount = 'amerennonserviceaccount',
  LandlordAgreements = 'landlordAgreements',
  AgreementLocations = 'agreementLocations',
  ServiceOrderHistory = 'ServiceOrderHistory',
  AvgMonthlyBill = 'AvgMonthlyBill',
  ConsumptionHistory = 'ConsumptionHistory',
  LoginRedirect = 'loginRedirect',
  SelectedAgreementNumber = 'selectedAgreementNumber',
  NewLocationAdded = 'NewLocationAdded',
  RefreshPremiseInfo = 'RefreshPremiseInfo',
  NewRegistrationInfo = 'NewRegistrationInfo',
}

type SessionManagerTypes = {
  [SessionManagerKey.AmerenNonServiceAccount]: string[] | null;
  [SessionManagerKey.LandlordAgreements]: Agreement[] | null;
  [SessionManagerKey.AgreementLocations]: PremiseInfo[] | null;
  [SessionManagerKey.LoginRedirect]: string | null;
  [SessionManagerKey.SelectedAgreementNumber]: number;
  [SessionManagerKey.NewLocationAdded]: boolean;
  [SessionManagerKey.RefreshPremiseInfo]: boolean;
  [SessionManagerKey.NewRegistrationInfo]: NewRegistrationInfo;
};

type StorageType = 'local' | 'session';

@Injectable({ providedIn: 'root' })
export class SessionManagerService {
  private getStorage(type: StorageType): Storage {
    return type === 'local' ? localStorage : sessionStorage;
  }

  // --- SET ---

  // 1. Typed for known keys
  set<K extends keyof SessionManagerTypes>(key: K, value: SessionManagerTypes[K], storageType?: StorageType): boolean;

  // 2. Generic for dynamic keys
  set<T = any>(key: string, value: T, storageType?: StorageType): boolean;

  set<T = any>(key: string, value: T, storageType: StorageType = 'session'): boolean {
    try {
      const storage = this.getStorage(storageType);
      if (value === undefined || value === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify(value));
      }
      return true;
    } catch (err) {
      return false;
    }
  }

  // --- GET ---

  // 1. Typed for known keys
  get<K extends keyof SessionManagerTypes>(key: K, storageType?: StorageType): SessionManagerTypes[K] | undefined;

  // 2. Generic for dynamic keys
  get<T = any>(key: string, storageType?: StorageType): T | undefined;

  get<T = any>(key: string, storageType: StorageType = 'session'): T | undefined {
    try {
      const item = this.getStorage(storageType).getItem(key);
      return item ? (JSON.parse(item) as T) : undefined;
    } catch (err) {
      return undefined;
    }
  }

  // --- HAS ---
  has(key: string, storageType: StorageType = 'session'): boolean {
    return this.getStorage(storageType).getItem(key) !== null;
  }

  // --- CLEAR ---
  clear(storageType: StorageType = 'session'): void {
    this.getStorage(storageType).clear();
  }
}
